import { TFunction } from "i18next";

import { IFilterWithValues } from "@/pages/Private/pages/List/components/FilterModal";
import { formatRelativeDays } from "@/pages/Private/pages/List/utils/relativeDateUtils";

import {
	LAST_CONTACT_OPTIONS,
	LastContactPreset,
	REENGAGE_FIELDS,
} from "../constants/reengage.constants";
import {
	ReengageOptionItem,
	ReengageSourceFormValues,
	emptyReengageForm,
} from "../schema/reengageSource";

type DropdownValue = { title: string; id: string }[];

const toLocalDate = (date: Date): string => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");

	return `${year}-${month}-${day}`;
};

const addDays = (base: Date, days: number): Date => {
	const result = new Date(base);

	result.setDate(result.getDate() + days);

	return result;
};

const today = (): Date => {
	const now = new Date();

	now.setHours(0, 0, 0, 0);

	return now;
};

const toDropdownValue = (items: ReengageOptionItem[]): DropdownValue =>
	items.map((item) => ({ title: item.title, id: String(item.id) }));

const presetDays = (preset: LastContactPreset): number | null =>
	LAST_CONTACT_OPTIONS.find((o) => o.id === preset)?.days ?? null;

/**
 * Builds the stored IFilterWithValues object from the curated re-engagement form. Empty
 * multi-selects are omitted entirely (meaning "all"). Dates are emitted as absolute strings;
 * the backend converts them to relative offsets on save.
 */
export const buildReengageFilter = (values: ReengageSourceFormValues): IFilterWithValues => {
	const filter: IFilterWithValues = {};

	const addDropdown = (field: { name: string; param: string }, items: ReengageOptionItem[]) => {
		if (items.length === 0) {
			return;
		}

		filter[field.name] = {
			value: toDropdownValue(items),
			filterParam: field.param,
			type: "dropdown",
		};
	};

	addDropdown(REENGAGE_FIELDS.fromList, values.fromLists);
	addDropdown(REENGAGE_FIELDS.bestSentiment, values.bestSentiment);
	addDropdown(REENGAGE_FIELDS.companyCategory, values.companyCategory);
	addDropdown(REENGAGE_FIELDS.personaCategory, values.personaCategory);
	addDropdown(REENGAGE_FIELDS.leadScore, values.leadScore);
	addDropdown(REENGAGE_FIELDS.companyHqLocation, values.companyHqLocation);
	addDropdown(REENGAGE_FIELDS.personalLocation, values.personalLocation);

	const lastContact = REENGAGE_FIELDS.lastContact;

	if (values.lastContact === "custom" && values.lastContactRange) {
		filter[lastContact.name] = {
			value: { from: values.lastContactRange.from, till: values.lastContactRange.till },
			filterParam: lastContact.param,
			type: "betweenDate",
		};
	} else {
		const days = presetDays(values.lastContact as LastContactPreset);

		if (days !== null) {
			filter[lastContact.name] = {
				value: toLocalDate(addDays(today(), -days)),
				filterParam: lastContact.param,
				type: "lessEqualDate",
			};
		}
	}

	return filter;
};

const asOptionItems = (raw: unknown): ReengageOptionItem[] => {
	if (!Array.isArray(raw)) {
		return [];
	}

	return raw
		.map((entry) => {
			const item = entry as { title?: string; name?: string; id?: string | number };
			const title = item.title ?? item.name;

			if (title === undefined || item.id === undefined) {
				return null;
			}

			return { id: item.id, title };
		})
		.filter((item): item is ReengageOptionItem => item !== null);
};

const matchPreset = (relativeDays?: number): LastContactPreset | null => {
	if (relativeDays === undefined) {
		return null;
	}

	return (
		LAST_CONTACT_OPTIONS.find((o) => o.days !== null && o.days === Math.abs(relativeDays))?.id ??
		null
	);
};

/** Parses a stored source filter (relative-encoded on GET) back into curated form values. */
export const parseReengageFilter = (
	filter: string | IFilterWithValues | null | undefined,
	name = ""
): ReengageSourceFormValues => {
	const values = emptyReengageForm(name);

	if (!filter) {
		return values;
	}

	let parsed: IFilterWithValues;

	try {
		parsed = typeof filter === "string" ? (JSON.parse(filter) as IFilterWithValues) : filter;
	} catch {
		return values;
	}

	values.fromLists = asOptionItems(parsed[REENGAGE_FIELDS.fromList.name]?.value);
	values.bestSentiment = asOptionItems(parsed[REENGAGE_FIELDS.bestSentiment.name]?.value);
	values.companyCategory = asOptionItems(parsed[REENGAGE_FIELDS.companyCategory.name]?.value);
	values.personaCategory = asOptionItems(parsed[REENGAGE_FIELDS.personaCategory.name]?.value);
	values.leadScore = asOptionItems(parsed[REENGAGE_FIELDS.leadScore.name]?.value);
	values.companyHqLocation = asOptionItems(parsed[REENGAGE_FIELDS.companyHqLocation.name]?.value);
	values.personalLocation = asOptionItems(parsed[REENGAGE_FIELDS.personalLocation.name]?.value);

	const lastContact = parsed[REENGAGE_FIELDS.lastContact.name];

	if (lastContact) {
		if (lastContact.type === "betweenDate") {
			values.lastContact = "custom";
			const base = today();
			const from =
				lastContact.relativeDaysFrom !== undefined
					? toLocalDate(addDays(base, lastContact.relativeDaysFrom))
					: ((lastContact.value as { from?: string })?.from ?? "");
			const till =
				lastContact.relativeDaysTill !== undefined
					? toLocalDate(addDays(base, lastContact.relativeDaysTill))
					: ((lastContact.value as { till?: string })?.till ?? "");

			values.lastContactRange = { from, till };
		} else {
			values.lastContact = matchPreset(lastContact.relativeDays) ?? "3m";
		}
	}

	return values;
};

/** Produces short, read-only chip labels describing a stored source filter. */
export const describeReengageFilter = (
	filter: string | IFilterWithValues | null | undefined,
	t: TFunction
): string[] => {
	const values = parseReengageFilter(filter);
	const chips: string[] = [];
	const ts = (key: string) => t(`list.reengage.${key}`);

	if (values.bestSentiment.length) {
		chips.push(`${ts("bestSentiment")}: ${values.bestSentiment.map((s) => s.title).join(", ")}`);
	}

	const lastContactDays = presetDays(values.lastContact as LastContactPreset);

	if (values.lastContact === "custom" && values.lastContactRange) {
		chips.push(
			`${ts("lastContact")}: ${values.lastContactRange.from} – ${values.lastContactRange.till}`
		);
	} else if (lastContactDays !== null) {
		chips.push(`${ts("lastContact")}: ${formatRelativeDays(-lastContactDays, t)}`);
	}

	if (values.fromLists.length) {
		chips.push(`${ts("fromList")}: ${values.fromLists.length}`);
	}

	if (values.companyCategory.length) {
		chips.push(`${ts("companyCategory")}: ${values.companyCategory.length}`);
	}

	if (values.personaCategory.length) {
		chips.push(`${ts("personaCategory")}: ${values.personaCategory.length}`);
	}

	if (values.leadScore.length) {
		chips.push(`${ts("leadScoring")}: ${values.leadScore.map((s) => s.title).join(", ")}`);
	}

	if (values.companyHqLocation.length) {
		chips.push(`${ts("companyHqLocation")}: ${values.companyHqLocation.length}`);
	}

	if (values.personalLocation.length) {
		chips.push(`${ts("personalLocation")}: ${values.personalLocation.length}`);
	}

	return chips;
};
